'use client';

import { ClerkProvider } from '@clerk/nextjs';
import { useClerkLocalization } from '@/hooks/use-clerk-localization';
import { ReactNode } from 'react';

// Conditionally import Clerk theme
let clerkTheme: any = null;
try {
  clerkTheme = require('@clerk/themes').dark;
} catch (error) {
  // Clerk themes not available, using default theme
}

interface DynamicClerkProviderProps {
  children: ReactNode;
}

export function DynamicClerkProvider({ children }: DynamicClerkProviderProps) {
  const clerkLocalization = useClerkLocalization();

  return (
    <ClerkProvider
      key={clerkLocalization ? 'da' : 'en'}
      localization={clerkLocalization}
      appearance={{
        baseTheme: clerkTheme,
        elements: {
          formButtonPrimary: "bg-green-600 hover:bg-green-700 text-white",
          card: "bg-white shadow-2xl border-0",
          headerTitle: "text-green-800 text-2xl font-bold",
          headerSubtitle: "text-gray-700",
          formFieldInput: "!border-2 !border-gray-300 focus:!border-green-500 focus:!ring-2 focus:!ring-green-200 !rounded-lg !px-4 !py-3 !text-sm !font-medium !transition-all !duration-200 !bg-white !shadow-sm hover:!border-gray-400",
          formFieldLabel: "font-semibold text-gray-700 mb-2 text-sm",
          formField: "mb-5",
          formFieldInput__emailAddress: "!border-2 !border-gray-300 focus:!border-green-500 !rounded-lg !px-4 !py-3 !bg-white !shadow-sm",
          formFieldInput__identifier: "!border-2 !border-gray-300 focus:!border-green-500 !rounded-lg !px-4 !py-3 !bg-white !shadow-sm",
          formFieldInputShowPasswordButton: "!border-l-2 !border-gray-300",
          footerActionLink: "text-green-600 hover:text-green-700",
          // OTP/Verification code input styling - CRITICAL for visibility
          otpCodeFieldInput: "!border-2 !border-gray-400 !bg-white !text-gray-900 !text-center !text-2xl !font-bold focus:!border-green-500 focus:!ring-2 focus:!ring-green-200 !rounded-lg !shadow-md",
          verificationCodeInput: "!border-2 !border-gray-400 !bg-white !text-gray-900 !text-center !text-2xl !font-bold focus:!border-green-500 focus:!ring-2 focus:!ring-green-200 !rounded-lg !shadow-md",
          // Enhanced social buttons styling with better visibility
          socialButtonsIconButton: "border-2 border-green-300 hover:border-green-500 hover:bg-green-50 shadow-md hover:shadow-lg transition-all duration-200 bg-white",
          socialButtonsBlockButton: "border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 shadow-lg hover:shadow-xl transition-all duration-200 bg-white font-semibold text-gray-700 hover:text-green-700 py-4 px-6 text-base",
          socialButtonsProviderIcon: "w-6 h-6",
          socialButtonsBlockButtonText: "font-semibold text-base ml-2",
          socialButtons: "space-y-4 my-6",
          socialButtonsBlockButtonArrow: "hidden", // Hide arrow for cleaner look
          footerActionText: "text-gray-700",
          dividerLine: "bg-gradient-to-r from-transparent via-gray-300 to-transparent",
          dividerText: "text-gray-600 font-medium bg-white px-4",
          // Additional styling for better visibility
          modalContent: "p-8",
          modalCloseButton: "text-gray-400 hover:text-gray-600"
        },
        variables: {
          colorPrimary: "#15803d", // green-700
          colorText: "#1f2937", // gray-800
          colorTextSecondary: "#4b5563", // gray-600
          colorBackground: "#ffffff",
          colorInputBackground: "#ffffff",
          colorInputText: "#1f2937",
          // Enhanced button styling
          borderRadius: "0.75rem", // More rounded corners
          spacingUnit: "1rem",
          // Increase font sizes for better visibility
          fontSize: "1rem"
        }
      }}
    >
      {children}
    </ClerkProvider>
  );
}